/** Small-visit merchants: merchants with many pickups but few orders per pickup. Own window, sort and CSV export. */
import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { Button, Panel, SegmentedControl, StatTile, tbl } from '../ui';
import { cn } from '../../utils/cn';
import { toCsv, downloadCsv } from '../../utils/csv';
import { getSmallVisitMerchants } from '../../services/analytics.api';
import type { SmallVisitMerchantRow } from '../../services/analytics.api';

const WINDOW_OPTIONS = [7, 30].map((d) => ({ value: d, label: `${d}d` }));
const PAGE_SIZE = 15;

type SortKey = 'merchant_name' | 'pickups' | 'orders' | 'avg_orders' | 'empty_pickups' | 'pickups_share_pct';
const COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: 'merchant_name', label: 'Merchant' },
  { key: 'pickups', label: 'Pickups' },
  { key: 'orders', label: 'Orders' },
  { key: 'avg_orders', label: 'Avg' },
  { key: 'empty_pickups', label: 'No-order' },
  { key: 'pickups_share_pct', label: 'Share' },
];

function ShareBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  const width = Math.min(100, Math.max(0, pct));
  return (
    <div className="flex items-center gap-2 text-[12px] text-gray-500">
      <span className="w-14 shrink-0">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div className={cn('h-full rounded-full', color)} style={{ width: `${width}%` }} />
      </div>
      <span className="w-12 text-right tabular-nums">{pct}%</span>
    </div>
  );
}

export default function SmallVisitMerchants() {
  const [days, setDays] = useState(30);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'pickups', dir: 'desc' });
  const [showAll, setShowAll] = useState(false);

  const query = useQuery({
    queryKey: ['analytics-small-visit', days],
    queryFn: () => getSmallVisitMerchants(days),
    refetchInterval: 15 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
  const report = query.data;
  const rows: SmallVisitMerchantRow[] = useMemo(() => report?.merchants ?? [], [report]);

  // Reset to the top page whenever the window changes — a stale "showing all 130" from a
  // wider window would otherwise carry over into a narrower one. Adjusting state during
  // render (React's documented pattern) rather than in an effect avoids the extra render pass.
  const [prevDays, setPrevDays] = useState(days);
  if (days !== prevDays) {
    setPrevDays(days);
    setShowAll(false);
  }

  const sorted = useMemo(() => {
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) =>
      key === 'merchant_name' ? mult * a.merchant_name.localeCompare(b.merchant_name) : mult * (a[key] - b[key])
    );
  }, [rows, sort]);
  const visible = showAll ? sorted : sorted.slice(0, PAGE_SIZE);
  const maxSharePct = Math.max(0, ...rows.map((m) => m.pickups_share_pct));

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'merchant_name' ? 'asc' : 'desc' }
    );
  };

  const exportCsv = () => {
    if (!report) return;
    const header = [
      'merchant_id',
      'merchant_name',
      'pickups',
      'orders',
      'avg_orders_per_pickup',
      'no_order_pickups',
      'share_of_pickups_pct',
      'window_from',
      'window_to',
    ];
    const body = sorted.map((m) => [
      m.merchant_id,
      m.merchant_name,
      m.pickups,
      m.orders,
      m.avg_orders,
      m.empty_pickups,
      m.pickups_share_pct,
      report.window.from_date,
      report.window.to_date,
    ]);
    downloadCsv(
      `small-visit-merchants_${report.window.from_date}_${report.window.to_date}.csv`,
      toCsv([header, ...body])
    );
  };

  const dimmed = query.isPlaceholderData;

  return (
    <Panel
      title="Small-visit merchants"
      subtitle={report ? `${report.rule.min_pickups}+ pickups averaging under ${report.rule.max_avg_orders} orders` : ' '}
      actions={
        <>
          <SegmentedControl label="Small-visit window" value={days} options={WINDOW_OPTIONS} onChange={setDays} />
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={query.isLoading || query.isPlaceholderData || rows.length === 0}
            onClick={exportCsv}
          >
            Export CSV
          </Button>
        </>
      }
    >
      {query.isLoading ? (
        <p className="flex items-center gap-2 py-2 text-[13px] text-gray-500">
          <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
          Loading…
        </p>
      ) : query.error ? (
        <p className="py-2 text-[13px] text-red-600">{(query.error as Error).message}</p>
      ) : rows.length === 0 ? (
        <p className={tbl.empty}>No small-visit merchants in this window.</p>
      ) : (
        <div aria-busy={dimmed} className={cn('motion-safe:transition-opacity', dimmed && 'opacity-60')}>
          <div className="mb-4 grid grid-cols-3 gap-3">
            <StatTile variant="inset" label="Flagged merchants" value={report?.flagged.merchants ?? 0} />
            <StatTile variant="inset" label="Share of pickups" value={report?.flagged.pickups_pct ?? 0} unit="%" />
            <StatTile variant="inset" label="Share of orders" value={report?.flagged.orders_pct ?? 0} unit="%" />
          </div>
          <div className="mb-4 space-y-1.5">
            <ShareBar label="pickups" pct={report?.flagged.pickups_pct ?? 0} color="bg-blue-500" />
            <ShareBar label="orders" pct={report?.flagged.orders_pct ?? 0} color="bg-amber-500" />
          </div>
          <div className={tbl.wrap}>
            <table className={tbl.table}>
              <thead>
                <tr className={tbl.headRow}>
                  {COLUMNS.map((col, i) => {
                    const active = sort.key === col.key;
                    return (
                      <th
                        key={col.key}
                        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        className={cn(tbl.th, i > 0 && 'text-right')}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort(col.key)}
                          className="inline-flex items-center gap-1 rounded hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        >
                          {col.label}
                          {active && <span aria-hidden className="text-[10px] text-gray-400">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                  <tr key={m.merchant_id} className={tbl.row}>
                    <td className={tbl.td}>
                      <span dir="auto">{m.merchant_name}</span>{' '}
                      <span className={tbl.muted}>#{m.merchant_id}</span>
                    </td>
                    <td className={cn(tbl.td, tbl.num)}>{m.pickups}</td>
                    <td className={cn(tbl.td, tbl.num)}>{m.orders}</td>
                    <td className={cn(tbl.td, tbl.num)}>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums',
                          m.avg_orders < 2 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
                        )}
                      >
                        {m.avg_orders}
                      </span>
                    </td>
                    <td className={cn(tbl.td, tbl.num)}>{m.empty_pickups}</td>
                    <td className={cn(tbl.td, tbl.num)}>
                      <div className="flex items-center justify-end gap-2">
                        <span className="tabular-nums">{m.pickups_share_pct}%</span>
                        <div className="h-1.5 w-10 shrink-0 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className="h-full rounded-full bg-blue-400"
                            style={{
                              width: `${maxSharePct > 0 ? Math.min(100, (m.pickups_share_pct / maxSharePct) * 100) : 0}%`,
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sorted.length > PAGE_SIZE && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 rounded text-[13px] text-blue-600 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {showAll ? 'Show fewer' : `Show all ${sorted.length}`}
            </button>
          )}
          <p className="mt-3 text-[12px] text-gray-500 text-pretty">
            Most no-order pickups have another pickup from the same merchant, with orders, around the same day — likely a duplicate record rather than a wasted trip.
          </p>
        </div>
      )}
    </Panel>
  );
}
